/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useContext } from "react";

// react-router components
import { useLocation, Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import SettingsIcon from "@mui/icons-material/Settings";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDBadge from "components/MDBadge";

// Material Dashboard 2 PRO React examples
import Breadcrumbs from "features/Breadcrumbs";
import UCNotificationItem from "application/components/UCNotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "features/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setDarkMode,
  setOpenPhone,
  setActivePage
} from "context";

function DashboardNavbar({ absolute, light, isMini, onPageChange }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode, openPhone, activePage } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState(null);
  const route = useLocation().pathname.split("/").slice(1);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);
  const handleOpenPhone = () => setOpenPhone(dispatch, !openPhone);

  const menuClicked = (page) => {
    if(page == "0") {
      activePage.key = "uc-call-back";
      activePage.name = "Call Back";
      activePage.url = "home/app/callback";
      activePage.description = "List of callbacks set for you by different users";
    } else if(page == "1") {
      activePage.key = "uc-call-history";
      activePage.name = "Call History";
      activePage.url = "home/app";
      activePage.description = "List of last 50 outgoing and incoming calls";
    }

    setActivePage(dispatch,activePage);
    setOpenMenu(false);
  }

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <Link onClick={() => menuClicked("0")} to="/app/home">
        <UCNotificationItem icon={<Icon>phone_callback</Icon>} title="Check for pending callbacks" />
      </Link>
      <Link onClick={() => menuClicked("1")} to="/app/home">
        <UCNotificationItem icon={<Icon>fax</Icon>} title="View received Fax" />
      </Link>
      <Link to="/dashboards/sales">
        <UCNotificationItem icon={<Icon>folder</Icon>} title="Browse files" />
      </Link>
      <Link to="/uc/messages">
        <UCNotificationItem icon={<Icon>email</Icon>} title="Messages" />
      </Link>
      <Link to="/uc/missed-calls">
        <UCNotificationItem icon={<Icon>call_missed</Icon>} title="View recent missed calls" />
      </Link>
      <Link to="/uc/phone-book">
        <UCNotificationItem icon={<Icon>contacts</Icon>} title="Phone Book" />
      </Link>
      <Link to="/uc/voicemail">
        <UCNotificationItem icon={<Icon>voicemail</Icon>} title="Listen to Voice mails" />
      </Link>
      <Link to="/uc/call-history">
        <UCNotificationItem icon={<Icon>history</Icon>} title="Call History" />
      </Link>
    </Menu>

  );

  // Styles for the navbar icons
  const iconsStyle = ({
    palette: { dark, white, text },
  }) => ({
    color: darkMode ? white.main : text.main,
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" description={activePage.description} title={activePage.name} route={activePage.url.split("/")} light={light} />
          <IconButton sx={navbarDesktopMenu} onClick={handleMiniSidenav} size="small" disableRipple>
            <Icon fontSize="medium" sx={iconsStyle}>
              {miniSidenav ? "menu_open" : "menu"}
            </Icon>
          </IconButton>
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox color={light ? "white" : "inherit"}>
              <Link to="/app/settings">
                <IconButton sx={navbarIconButton} size="small" disableRipple>
                  <Icon sx={iconsStyle}>account_circle</Icon>
                </IconButton>
              </Link>
              <IconButton sx={navbarIconButton} size="small" onClick={handleOpenPhone} disableRipple>
                <Icon sx={iconsStyle}>phone</Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              <Tooltip title="Settings">
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  onClick={(e) => setSettingsMenuAnchor(e.currentTarget)}
                >
                  <Icon sx={iconsStyle}>settings</Icon>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={settingsMenuAnchor}
                open={Boolean(settingsMenuAnchor)}
                onClose={() => setSettingsMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem
                  onClick={() => {
                    setDarkMode(dispatch, !darkMode);
                    setSettingsMenuAnchor(null);
                  }}
                >
                  <ListItemIcon sx={{ color: "inherit" }}>
                    {darkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText primary={darkMode ? "Light Mode" : "Dark Mode"} />
                </MenuItem>
              </Menu>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <MDBadge badgeContent={9} color="error" size="xs" circular>
                  <Icon sx={iconsStyle}>notifications</Icon>
                </MDBadge>
              </IconButton>
              {renderMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
